import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PeakBrandMark from "./onboarding/PeakBrandMark";
import { safeGetItem, safeSetItem } from "../utils/localStorageUtils";
import {
  NATIVE_APP_PROMPT_DISMISSED_KEY,
  isHandheldDevice,
  shouldPreferNativeApp,
} from "../utils/nativeAppPrompt";
import appIcon from "../assets/download/peak-app-icon.png";
import appStoreBadge from "../assets/download/app-store-badge.png";
import googlePlayBadge from "../assets/download/google-play-badge.png";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=app.peak.peakandroid";
const APP_STORE_URL = "https://apps.apple.com/app/peak/id6784633966";

/**
 * Full-screen prompt for signed-in athletes on a phone or tablet.
 * Desktop browsers never see it. Dismissing writes a localStorage flag.
 */
const NativeAppPrompt: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(
    () => safeGetItem(NATIVE_APP_PROMPT_DISMISSED_KEY) === "1",
  );

  const visible =
    !dismissed &&
    shouldPreferNativeApp({
      hasUser: Boolean(currentUser),
      pathname: location.pathname,
      search: location.search,
      handheld: isHandheldDevice(),
    });

  useEffect(() => {
    if (!visible) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    safeSetItem(NATIVE_APP_PROMPT_DISMISSED_KEY, "1");
    setDismissed(true);
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="native-app-prompt-title"
      className="fixed inset-0 z-[10000000] flex min-h-[100dvh] flex-col bg-base-100 text-base-content"
      style={{
        paddingTop: "env(safe-area-inset-top)",
      }}
    >
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-4">
            <img
              src={appIcon}
              alt=""
              width={72}
              height={72}
              className="h-[72px] w-[72px] rounded-[22%] shadow-[0_4px_12px_rgba(0,90,122,0.25)]"
            />
            <PeakBrandMark enlarged />
          </div>
          <h1
            id="native-app-prompt-title"
            className="mt-8 font-heading text-3xl tracking-tight"
          >
            The native apps are live
          </h1>
          <div className="mt-3 flex flex-col gap-1 font-grotesk leading-relaxed text-base-content/80">
            <p>Download PEAK and sign in there for the better experience.</p>
            {currentUser?.email && (
              <div className="mt-2 flex flex-col gap-1.5">
                <span className="text-xs font-medium text-base-content/60">
                  Your account email
                </span>
                <p className="w-fit max-w-full truncate rounded-md border border-base-300 bg-base-200 px-3 py-1.5 text-sm font-medium text-base-content">
                  {currentUser.email}
                </p>
              </div>
            )}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-[10px] transition-transform active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                width={156}
                height={52}
                className="h-[52px] w-auto"
              />
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-[10px] transition-transform active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <img
                src={googlePlayBadge}
                alt="Get it on Google Play"
                width={175}
                height={52}
                className="h-[52px] w-auto"
              />
            </a>
          </div>
        </div>
      </div>
      <div
        className="flex justify-center px-6 pt-2"
        style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
      >
        <button
          type="button"
          onClick={dismiss}
          className="px-3 py-2 text-sm font-grotesk text-base-content/60 underline-offset-2 hover:text-base-content hover:underline"
        >
          No thanks, use here
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default NativeAppPrompt;
